import SideNavigation from 'components/learningPlan/SideNavigation';
import { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getLearningPlanAction } from 'store/modules/learningPlan/learningPlan-action';
import { ReducerStatus } from 'utils/constants';
import {
  handleErrorAndNavigate,
  createErrorInfoFromAction,
} from 'utils/errorHandler';
import LearningPlanHeader from 'components/learningPlan/LearningPlanHeader';

import {
  Flex,
  IconButton,
  Box,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Button,
  Show,
  Stack,
  Skeleton,
} from '@chakra-ui/react';
import { MdSettings } from 'react-icons/md';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { learningPlanActions } from 'store/modules/learningPlan/learningPlan-slice';
import UpdateLearningPlanMd from 'components/learningPlan/modals/UpdateLearningPlanMd';
import DeleteLearningPlanMd from 'components/learningPlan/modals/DeleteLearningPlanMd';
import SideNavigationSm from 'components/learningPlan/SideNavigationSm';
import { FaList } from 'react-icons/fa';
import { getLearningPlanCoursesAction } from 'store/modules/learningPlanCourse/learningPlanCourse-action';
import LearningPlanCourseList from 'components/learningPlan/LearningPlanCourseList';
import DeleteLearningPlanCourseMd from 'components/learningPlan/modals/DeleteLearningPlanCourseMd';
import { learningPlanCourseActions } from 'store/modules/learningPlanCourse/learningPlanCourse-slice';
import AddLearningPlanCoursesMd from 'components/learningPlan/modals/AddLearningPlanCoursesMd';
import { publishEntityActions } from 'store/modules/common/publishEntity/publishEntity-slice';

function LearningPlanProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const learningPlanEntityConfig = useMemo(
    () => ({
      name: 'Learning Plan',
      homeRoute: '/admin/learning-plans',
    }),
    []
  );

  const learningPlan = useSelector((state) => state.learningPlan.learningPlan);
  const learningPlanCourses = useSelector(
    (state) => state.learningPlanCourse.learningPlanCourses
  );
  const getLearningPlan = useSelector(
    (state) => state.learningPlan.getLearningPlan
  );

  const getLearningPlanCourses = useSelector(
    (state) => state.learningPlanCourse.getLearningPlanCourses
  );

  const deleteLearningPlan = useSelector(
    (state) => state.learningPlan.deleteLearningPlan
  );

  const publishStatus = useSelector(
    (state) => state.publishEntity.updatePublishEntity.status
  );

  const handleAddButton = () => {
    dispatch(
      learningPlanCourseActions.setAddLearningPlanCourses({ modal: true, id })
    );
  };

  const handleEditButton = () => {
    dispatch(learningPlanActions.setUpdateLearningPlan({ modal: true, id }));
  };

  const handleDeleteButton = () => {
    dispatch(learningPlanActions.setDeleteLearningPlan({ modal: true, id }));
  };

  useEffect(() => {
    dispatch(getLearningPlanAction(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (deleteLearningPlan.status === ReducerStatus.SUCCEEDED) {
      navigate('/admin/learning-plans');
    }
  }, [deleteLearningPlan.status, navigate]);

  useEffect(() => {
    if (learningPlan) {
      document.title = `Skill Quest | Admin - ${learningPlan.name}`;
    }
  }, [learningPlan]);

  useEffect(() => {
    if (getLearningPlan.status === ReducerStatus.FAILED) {
      // Create standardized error info from Redux action failure
      const errorInfo = createErrorInfoFromAction(
        { payload: getLearningPlan.error },
        'Learning Plan Not Found',
        'The learning plan you are looking for does not exist or may have been removed.'
      );

      // Handle error and navigate using utility function
      handleErrorAndNavigate(
        dispatch,
        navigate,
        location,
        errorInfo,
        learningPlanEntityConfig
      );
    } else if (getLearningPlan.status === ReducerStatus.SUCCEEDED) {
      dispatch(getLearningPlanCoursesAction(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getLearningPlan.status,
    getLearningPlan.error,
    id,
    dispatch,
    navigate,
    learningPlanEntityConfig,
  ]);

  // Reset publish status after SUCCESS/FAIL
  useEffect(() => {
    if (
      publishStatus === ReducerStatus.SUCCEEDED ||
      publishStatus === ReducerStatus.FAILED
    ) {
      dispatch(publishEntityActions.resetPublishStatus());
    }
  }, [publishStatus, dispatch]);

  return (
    <Flex ml={{ base: 0, md: 20 }} minH="100vh">
      <SideNavigationSm
        isOpen={isOpen}
        onClose={onClose}
        id={parseInt(id, 10)}
      />
      <AddLearningPlanCoursesMd />
      <UpdateLearningPlanMd />
      <DeleteLearningPlanMd />
      <DeleteLearningPlanCourseMd />
      <SideNavigation id={parseInt(id, 10)} />
      <Flex width="100%" direction="column" p={10}>
        <Flex mb={4} direction={{ base: 'column', sm: 'row' }}>
          <LearningPlanHeader
            status={getLearningPlan.status}
            learningPlan={learningPlan}
            publishStatus={publishStatus}
            flex={1}
          />
          <Menu>
            <MenuButton
              as={IconButton}
              size={{ base: 'md', md: 'sm' }}
              colorScheme="teal"
              aria-label="Options"
              icon={<MdSettings />}
              variant="outline"
            />
            <MenuList>
              <MenuItem icon={<EditIcon />} onClick={handleEditButton}>
                Edit Learning Plan
              </MenuItem>
              <MenuItem icon={<DeleteIcon />} onClick={handleDeleteButton}>
                Delete Learning Plan
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        <Box>
          <Flex mb={4} wrap="wrap" gap={2} justify="end">
            {getLearningPlan.status !== ReducerStatus.LOADING && (
              <Show below="lg">
                <Button
                  leftIcon={<FaList />}
                  size="md"
                  colorScheme="blue"
                  variant="outline"
                  onClick={onOpen}
                >
                  Plans
                </Button>
              </Show>
            )}

            {getLearningPlan.status !== ReducerStatus.LOADING &&
              getLearningPlanCourses.status !== ReducerStatus.LOADING && (
                <Button
                  leftIcon={<AddIcon />}
                  size="md"
                  colorScheme="red"
                  onClick={handleAddButton}
                >
                  Add Courses
                </Button>
              )}
          </Flex>

          {getLearningPlan.status === ReducerStatus.LOADING ||
          getLearningPlanCourses.status === ReducerStatus.LOADING ? (
            <Stack gap={4}>
              {[...Array(5)].map(() => (
                <Skeleton
                  key={uuidv4()}
                  w="100%"
                  height="65px"
                  borderRadius="md"
                />
              ))}
            </Stack>
          ) : (
            <LearningPlanCourseList learningPlanCourses={learningPlanCourses} />
          )}
        </Box>
      </Flex>
    </Flex>
  );
}

export default LearningPlanProfilePage;
